import { AsyncSelect, Button, FileUploader, FlexContent, FormField, Input, ModalDialog, Select, TableCell, TableHeadCell, TableRow, Textarea } from "@rothoblaas/design-system";
import { ChangeEvent, DragEvent, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import ExpansionPanel from "../../../../components/ui/ExpansionPanel";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Table from "../../../../components/ui/Table";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { Admin3DModelEntityType, Admin3DModelRecord, Admin3DModelUploadEntry } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";
import { formatFileSize } from "../../../../utils/fileSize";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import Model3DPreview, {
  ConnectorGender,
  ConnectorLock,
  ConnectorModule,
  HoleFeature,
  ModuleLayout,
  expandHolesPattern,
} from "./Model3DPreview";

type UploadState = "queued" | "uploading" | "success" | "error";

type UploadRow = Admin3DModelUploadEntry & {
  status: UploadState;
  progress: number;
  error?: string;
};

type SelectOption = { value: string; label: string; moduleCount?: number; moduleWidth?: number };
type RotationTuple = [number, number, number];

const acceptedExtensions = [".glb"];

const is3DModelFile = (file: File) =>
  acceptedExtensions.some((extension) => file.name.toLowerCase().endsWith(extension));

const newClientId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getModelFormat = (fileName: string) => fileName.split(".").pop()?.toLowerCase() ?? "";

const entityLabels: Record<Admin3DModelEntityType, string> = {
  lock_3d: "Lock",
  lockstop_3d: "Lockstop",
  fastener_3d: "Fastener",
};

const modelTypeOptions = Object.entries(entityLabels).map(([value, label]) => ({
  value: value as Admin3DModelEntityType,
  label,
}));

const parseFeatures = (value: string): Record<string, unknown> | null => {
  const trimmed = value.trim();
  return trimmed ? (JSON.parse(trimmed) as Record<string, unknown>) : null;
};

const serializeFeatures = (features?: Record<string, unknown> | null) =>
  features ? JSON.stringify(features, null, 2) : "";

const Admin3DModelsTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const inputRef = useRef<HTMLInputElement>(null);
  const aborter = useRef(new AbortController());
  const uploadedModelsFilters = useListFilters({ isSearchable: true, prefix: "uploaded3DModels", limit: 20 });

  const [modelType, setModelType] = useState<Admin3DModelEntityType>("lock_3d");
  const [features, setFeatures] = useState("{}");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [modelToEdit, setModelToEdit] = useState<Admin3DModelRecord>();
  const [modelToDelete, setModelToDelete] = useState<Admin3DModelRecord>();
  const [editFeatures, setEditFeatures] = useState("");

  const [editFile, setEditFile] = useState<File>();
  const [editFileName, setEditFileName] = useState("");
  const [editModelType, setEditModelType] = useState<Admin3DModelEntityType>("lock_3d");
  const [associationItemId, setAssociationItemId] = useState("");
  const [associationOptions, setAssociationOptions] = useState<SelectOption[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const uploadedModelsQuery: PaginationQuery = { ...uploadedModelsFilters.state, q: uploadedModelsFilters.state.q };
  const { data, error } = adminApi.use3DModels(undefined, uploadedModelsQuery, aborter.current.signal);
  const queuedRows = useMemo(() => rows.filter((row) => row.status === "queued"), [rows]);
  const activeUploadRows = useMemo(
    () => rows.filter((row) => row.status === "queued" || row.status === "uploading"),
    [rows],
  );

  // 1. PARSING DEL JSON
  const [parsedEditFeatures, hasInvalidFeatures] = useMemo(() => {
    try {
      return [parseFeatures(editFeatures), false] as const;
    } catch {
      return [null, true] as const;
    }
  }, [editFeatures]);

  // 2. MODULE LAYOUT (PATTERNS)
  const moduleLayout: ModuleLayout = useMemo(() => {
    const layout = (parsedEditFeatures?.["hole_module_layout"] ?? parsedEditFeatures?.["pattern_layout"]) as any;
    return {
      count: typeof layout?.count === "number" && layout.count > 0 ? layout.count : 1,
      pitch_mm: typeof layout?.pitch_mm === "number" ? layout.pitch_mm : 25,
      repeat_axis:
        Array.isArray(layout?.repeat_axis) && layout.repeat_axis.length === 3 ? layout.repeat_axis : [1, 0, 0],
      placement: layout?.placement === "start" ? "start" : "centered",
    };
  }, [parsedEditFeatures]);

  // 3. CONNECTOR MODULE (SINGOLO MODULO BASE)
  const connectorModuleData: ConnectorModule = useMemo(() => {
    const moduleObj = parsedEditFeatures?.["connector_module"] as ConnectorModule | undefined;
    return {
      width: typeof moduleObj?.width === "number" ? moduleObj.width : 25,
      height: typeof moduleObj?.height === "number" ? moduleObj.height : undefined,
      thickness: typeof moduleObj?.thickness === "number" ? moduleObj.thickness : undefined,
      side: moduleObj?.side ?? "female",
    };
  }, [parsedEditFeatures]);

  // 4. CONNECTOR LOCK (ASSIEME COMPLESSIVO)
  const connectorData: ConnectorLock = useMemo(() => {
    const conn = parsedEditFeatures?.["connector"] as ConnectorLock | undefined;
    const side = conn?.side ?? connectorModuleData.side ?? "female";
    return {
      id: conn?.id ?? "connector_primary",
      side: side,
      width: typeof conn?.width === "number" ? conn.width : (connectorModuleData.width ?? 25) * moduleLayout.count!,
      height: typeof conn?.height === "number" ? conn.height : connectorModuleData.height,
      thickness: typeof conn?.thickness === "number" ? conn.thickness : connectorModuleData.thickness,
      position: Array.isArray(conn?.position) && conn.position.length === 3 ? conn.position : [0, 0, 0],
      normal: Array.isArray(conn?.normal) && conn.normal.length === 3 ? conn.normal : [0, 0, 1],
    };
  }, [parsedEditFeatures, connectorModuleData, moduleLayout.count]);

  // 5. PATTERN BASE PER UN SINGOLO MODULO
  const holesPattern: HoleFeature[] = useMemo(() => {
    const rawPattern = Array.isArray(parsedEditFeatures?.["holes_pattern"])
      ? parsedEditFeatures!["holes_pattern"]
      : Array.isArray(parsedEditFeatures?.["holes"])
        ? parsedEditFeatures!["holes"]
        : [];

    return rawPattern.map((h: any, idx: number) => {
      const pos =
        Array.isArray(h.position) && h.position.length === 3
          ? h.position
          : Array.isArray(h.center_mm) && h.center_mm.length === 3
            ? h.center_mm
            : [0, 0, 0];

      const norm = Array.isArray(h.normal) && h.normal.length === 3 ? h.normal : [0, 0, 1];
      const isInclined = Math.abs(norm[0]) > 0.01 || Math.abs(norm[1]) > 0.01;
      const holeType = h.type ?? (isInclined ? "inclined_45deg" : "standard");

      return {
        id: h.id ?? idx + 1,
        type: holeType,
        position: pos,
        diameter_mm: typeof h.diameter_mm === "number" ? h.diameter_mm : undefined,
        radius_mm: typeof h.radius_mm === "number" ? h.radius_mm : undefined,
        thickness: typeof h.thickness === "number" ? h.thickness : undefined,
        normal: norm,
        applicable_sides: Array.isArray(h.applicable_sides) ? h.applicable_sides : undefined,
      };
    });
  }, [parsedEditFeatures]);

  // 6. CALCOLO FINALE DEI FORI ESPANSI CON OFFSET E COUNT
  const calculatedHoles: HoleFeature[] = useMemo(() => {
    return expandHolesPattern(holesPattern, moduleLayout);
  }, [holesPattern, moduleLayout]);

  // ROTAZIONI
  const componentRotation: RotationTuple = useMemo(() => {
    const rotObj = parsedEditFeatures?.["rotation"] as Record<string, unknown> | undefined;
    const compRot = rotObj?.["component_rotation_deg"];
    if (
      Array.isArray(compRot) &&
      compRot.length === 3 &&
      compRot.every((v) => typeof v === "number" && Number.isFinite(v))
    ) {
      return [compRot[0], compRot[1], compRot[2]] as RotationTuple;
    }
    return [0, 0, 0];
  }, [parsedEditFeatures]);

  const holesRotation: RotationTuple = useMemo(() => {
    const rotObj = parsedEditFeatures?.["rotation"] as Record<string, unknown> | undefined;
    const hRot = rotObj?.["holes_rotation_deg"];
    if (Array.isArray(hRot) && hRot.length === 3 && hRot.every((v) => typeof v === "number" && Number.isFinite(v))) {
      return [hRot[0], hRot[1], hRot[2]] as RotationTuple;
    }
    return [0, 0, 0];
  }, [parsedEditFeatures]);

  // AGGIORNAMENTO ROTAZIONI NEL JSON
  const updateRotationInFeaturesJson = (target: "component" | "holes", axisIndex: 0 | 1 | 2, newValue: number) => {
    try {
      const currentJson = parseFeatures(editFeatures) ?? {};
      const currentRotation = (currentJson["rotation"] as Record<string, unknown>) ?? {};

      const nextCompRot: RotationTuple = [...componentRotation];
      const nextHolesRot: RotationTuple = [...holesRotation];
      const validVal = Number.isFinite(newValue) ? newValue : 0;

      if (target === "component") nextCompRot[axisIndex] = validVal;
      else nextHolesRot[axisIndex] = validVal;

      const updatedFeatures = {
        ...currentJson,
        rotation: {
          ...currentRotation,
          component_rotation_deg: nextCompRot,
          holes_rotation_deg: nextHolesRot,
        },
      };

      setEditFeatures(JSON.stringify(updatedFeatures, null, 2));
    } catch {
      dispatch(showErrorNotification(i18n, new Error("Correggere il JSON prima di modificare le rotazioni.")));
    }
  };

  // AGGIORNAMENTO LAYOUT / PATTERNS NEL JSON
  const updateLayoutInFeaturesJson = (field: keyof ModuleLayout, value: any) => {
    try {
      const currentJson = parseFeatures(editFeatures) ?? {};
      const currentLayout = (currentJson["hole_module_layout"] as Record<string, unknown>) ?? {};

      const nextLayout = {
        ...currentLayout,
        [field]: value,
      };

      const newLayoutObj: ModuleLayout = { ...moduleLayout, [field]: value };
      const newExpandedHoles = expandHolesPattern(holesPattern, newLayoutObj);

      const updatedFeatures = {
        ...currentJson,
        hole_module_layout: nextLayout,
        holes_pattern: holesPattern,
        holes: newExpandedHoles,
      };

      setEditFeatures(JSON.stringify(updatedFeatures, null, 2));
    } catch {
      dispatch(showErrorNotification(i18n, new Error("Correggere il JSON prima di modificare il layout.")));
    }
  };

  // AGGIORNAMENTO GENERE CONNETTORE (MASCHIO / FEMMINA) NEL JSON
  // Funzione aggiornata per scrivere 'side' in UN SOLO POSTO (in connector_module)
  const updateConnectorSideInFeaturesJson = (newSide: ConnectorGender) => {
    try {
      const currentJson = parseFeatures(editFeatures) ?? {};
      const currentModule = (currentJson["connector_module"] as Record<string, unknown>) ?? {};

      const updatedFeatures = {
        ...currentJson,
        connector_module: {
          ...currentModule,
          side: newSide, // Salva solo qui!
        },
      };

      setEditFeatures(JSON.stringify(updatedFeatures, null, 2));
    } catch {
      dispatch(showErrorNotification(i18n, new Error("Errore nel JSON.")));
    }
  };

  useEffect(() => {
    if (!modelToEdit) return;

    const signal = new AbortController().signal;
    const loadOptions = async () => {
      try {
        switch (editModelType) {
          case "lock_3d": {
            const items = await adminApi.getLocks({ q: "", page: 1, limit: 400 }, signal);
            setAssociationOptions(
              items.map((item) => ({
                value: item.item.id,
                label: item.item.code,
                moduleCount: item.nModules,
                moduleWidth: item.lockModule?.moduleWidth,
              })),
            );
            break;
          }
          case "lockstop_3d": {
            const items = await adminApi.getLockStops({ q: "", page: 1, limit: 400 }, signal);
            setAssociationOptions(items.map((item) => ({ value: item.item.id, label: item.item.code })));
            break;
          }
          case "fastener_3d": {
            const items = await adminApi.getFasteners({ q: "", page: 1, limit: 400 }, signal);
            setAssociationOptions(items.map((item) => ({ value: item.item.id, label: item.item.code })));
            break;
          }
        }
      } catch {
        setAssociationOptions([]);
      }
    };

    void loadOptions();
  }, [editModelType, modelToEdit]);

  useEffect(() => {
    if (!modelToEdit) {
      setPreviewUrl(undefined);
      return;
    }

    let active = true;
    let objectUrl: string | undefined;
    const loadPreview = async () => {
      if (editFile) {
        objectUrl = URL.createObjectURL(editFile);
      } else if (modelToEdit.modelFormat.toLowerCase() === "glb") {
        objectUrl = await adminApi.get3DModelObjectUrl(modelToEdit);
      }
      if (active) setPreviewUrl(objectUrl);
      else if (objectUrl) URL.revokeObjectURL(objectUrl);
    };

    void loadPreview().catch(() => {
      if (active) setPreviewUrl(undefined);
    });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [editFile, modelToEdit]);

  const searchAssociationOptions = (type: Admin3DModelEntityType, query: string) => {
    const signal = new AbortController().signal;
    switch (type) {
      case "lock_3d":
        return adminApi.getLocks({ q: query, page: 1, limit: 20 }, signal).then((items) =>
          items.map((item) => ({
            value: item.item.id,
            label: item.item.code,
            moduleCount: item.nModules,
            moduleWidth: item.lockModule?.moduleWidth,
          })),
        );
      case "lockstop_3d":
        return adminApi
          .getLockStops({ q: query, page: 1, limit: 20 }, signal)
          .then((items) => items.map((item) => ({ value: item.item.id, label: item.item.code })));
      case "fastener_3d":
        return adminApi
          .getFasteners({ q: query, page: 1, limit: 20 }, signal)
          .then((items) => items.map((item) => ({ value: item.item.id, label: item.item.code })));
    }
  };

  const addFiles = (files: FileList | File[]) => {
    let parsedFeatures: Record<string, unknown> | null;
    try {
      parsedFeatures = parseFeatures(features);
    } catch {
      dispatch(showErrorNotification(i18n, new Error("Features must contain valid JSON.")));
      return;
    }

    const nextRows = Array.from(files).map<UploadRow>((file) => ({
      clientId: newClientId(),
      modelType,
      features: parsedFeatures,
      file,
      status: is3DModelFile(file) ? "queued" : "error",
      progress: 0,
      error: !is3DModelFile(file) ? "Only .glb files are supported." : undefined,
    }));
    setRows((currentRows) => [...currentRows, ...nextRows]);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) addFiles(event.target.files);
    event.target.value = "";
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  };

  const updateQueuedRow = (clientId: string, changes: Partial<Pick<UploadRow, "modelType">>) => {
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.clientId === clientId ? { ...row, ...changes, status: "queued", progress: 0, error: undefined } : row,
      ),
    );
  };

  const uploadQueuedRows = async () => {
    if (!queuedRows.length) return;
    const validRows = queuedRows.filter((row) => is3DModelFile(row.file));
    if (!validRows.length) return;
    setIsUploading(true);
    setRows((currentRows) =>
      currentRows.map((row) =>
        validRows.some((validRow) => validRow.clientId === row.clientId)
          ? { ...row, status: "uploading", progress: 10 }
          : row,
      ),
    );
    try {
      const results = await adminApi.upload3DModels(validRows, (progress) => {
        setRows((currentRows) =>
          currentRows.map((row) =>
            validRows.some((validRow) => validRow.clientId === row.clientId) ? { ...row, progress } : row,
          ),
        );
      });
      const resultsByClientId = new Map(results.map((result) => [result.clientId, result]));
      setRows((currentRows) =>
        currentRows.map((row) => {
          const result = resultsByClientId.get(row.clientId);
          if (!result) return row;
          return {
            ...row,
            status: result.success ? "success" : "error",
            progress: 100,
            error: result.error,
          };
        }),
      );
      if (results.some((result) => result.success)) dispatch(showSuccessNotification(i18n.translation));
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : "The upload request failed.";
      setRows((currentRows) =>
        currentRows.map((row) =>
          row.status === "uploading" ? { ...row, status: "error", progress: 100, error: message } : row,
        ),
      );
      dispatch(showErrorNotification(i18n, uploadError));
    } finally {
      setIsUploading(false);
    }
  };

  const openEdit = (model: Admin3DModelRecord) => {
    setModelToEdit(model);
    setPreviewUrl(undefined);
    setEditFeatures(serializeFeatures(model.features));
    setEditFile(undefined);
    setEditFileName(model.modelFile);
    setEditModelType(model.modelType);
    setAssociationItemId(model.itemId ?? "");
  };

  const saveEdit = async () => {
    if (!modelToEdit) return;
    let parsedFeatures: Record<string, unknown> | null;
    try {
      parsedFeatures = parseFeatures(editFeatures);
    } catch {
      dispatch(showErrorNotification(i18n, new Error("Features must contain valid JSON.")));
      return;
    }
    setIsSavingEdit(true);
    try {
      await adminApi.update3DModel(modelToEdit.modelType, modelToEdit.modelId, {
        modelType: editModelType,
        itemId: associationItemId || null,
        modelFile: editFileName,
        features: parsedFeatures,
        file: editFile,
      });
      setModelToEdit(undefined);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (updateError) {
      dispatch(showErrorNotification(i18n, updateError));
    } finally {
      setIsSavingEdit(false);
    }
  };

  const deleteModel = async () => {
    if (!modelToDelete) return;
    try {
      await adminApi.delete3DModel(modelToDelete.modelType, modelToDelete.modelId);
      setModelToDelete(undefined);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (deleteError) {
      dispatch(showErrorNotification(i18n, deleteError));
    }
  };

  return (
    <>
      <FlexContent padding direction="row">
        <FlexContent direction="column">
          <FormFieldsContainer>
            <FormField label="3D model type">
              <Select
                value={modelTypeOptions.find((option) => option.value === modelType)}
                options={modelTypeOptions}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={(option) => option && setModelType(option.value)}
                isClearable={false}
              />
            </FormField>
            <FormField label="Features (JSON)">
              <Textarea value={features} onChange={(event) => setFeatures(event.target.value)} />
            </FormField>

            <div
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(event) => event.key === "Enter" && inputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              style={{
                border: `1px dashed ${isDragging ? "#1884a5" : "#9ca3af"}`,
                borderRadius: 4,
                padding: 24,
                cursor: "pointer",
                textAlign: "center",
              }}
            >
              {isDragging ? "Drop .glb files here" : "Drag and drop .glb files here, or click to select multiple files"}
              <input ref={inputRef} type="file" accept=".glb" multiple hidden onChange={onFileChange} />
            </div>
            <Button disabled={!queuedRows.length || isUploading} onClick={uploadQueuedRows}>
              Upload queued files ({queuedRows.length})
            </Button>
          </FormFieldsContainer>
        </FlexContent>

        <FlexContent padding noPaddingTop>
          <ExpansionPanel title={`Uploaded models (${data?.total ?? 0})`} defaultExpanded>
            <div style={{ maxHeight: "calc(100vh - 18rem)", overflowY: "auto" }}>
              <Table<Admin3DModelRecord>
                filters={uploadedModelsFilters}
                search={{
                  placeholder: `${i18n.translation.common.search}...`,
                  onChange: () => {
                    aborter.current.abort();
                    aborter.current = new AbortController();
                  },
                }}
                head={
                  <TableRow>
                    <TableHeadCell align="left">File</TableHeadCell>
                    <TableHeadCell align="left">Type</TableHeadCell>
                    <TableHeadCell align="left">Item ID</TableHeadCell>
                    <TableHeadCell align="left">Format</TableHeadCell>
                    <TableHeadCell align="left">Size</TableHeadCell>
                    <TableHeadCell align="left">Features</TableHeadCell>
                    <TableHeadCell align="left"></TableHeadCell>
                  </TableRow>
                }
                items={data?.items ?? []}
                total={data?.total ?? 0}
                error={!!error}
                renderRow={(model) => (
                  <>
                    <TableCell>{model.modelFile}</TableCell>
                    <TableCell>{entityLabels[model.modelType]}</TableCell>
                    <TableCell>{model.itemId ?? "Not associated"}</TableCell>
                    <TableCell>{model.modelFormat.toUpperCase()}</TableCell>
                    <TableCell>{formatFileSize(model.dataSize)}</TableCell>
                    <TableCell>{serializeFeatures(model.features)}</TableCell>
                  </>
                )}
                rowActions={(model) => [
                  getEditAction(i18n, () => openEdit(model)),
                  getDeleteAction(i18n, () => setModelToDelete(model), true),
                ]}
              />
            </div>
          </ExpansionPanel>
          <ExpansionPanel title={`Files in upload (${activeUploadRows.length})`} defaultExpanded>
            <Table<UploadRow>
              head={
                <TableRow>
                  <TableHeadCell align="left">File</TableHeadCell>
                  <TableHeadCell align="left">Model type</TableHeadCell>
                  <TableHeadCell align="left">Status</TableHeadCell>
                  <TableHeadCell align="left">Progress</TableHeadCell>
                  <TableHeadCell align="left">Error</TableHeadCell>
                </TableRow>
              }
              items={rows}
              total={rows.length}
              renderRow={(row) => (
                <>
                  <TableCell>{row.file.name}</TableCell>
                  <TableCell>
                    {row.status === "queued" || row.status === "error" ? (
                      <select
                        value={row.modelType}
                        onChange={(event) =>
                          updateQueuedRow(row.clientId, { modelType: event.target.value as Admin3DModelEntityType })
                        }
                      >
                        {Object.entries(entityLabels).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      entityLabels[row.modelType]
                    )}
                  </TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>
                    <progress value={row.progress} max={100} /> {row.progress}%
                  </TableCell>
                  <TableCell>{row.error ?? ""}</TableCell>
                </>
              )}
            />
          </ExpansionPanel>
        </FlexContent>
      </FlexContent>

      {modelToEdit && (
        <ModalDialog
          title={`Edit ${modelToEdit.modelFile}`}
          onClose={() => setModelToEdit(undefined)}
          isSubmitting={isSavingEdit}
          buttons={[
            <Button key={0} variant="secondary" onClick={() => setModelToEdit(undefined)}>
              {i18n.translation.common.cancel}
            </Button>,
            <Button key={1} onClick={saveEdit}>
              {i18n.translation.common.save}
            </Button>,
          ]}
        >
          <FormFieldsContainer>
            <FormField label="File name">
              <Input value={editFileName} onChange={(event) => setEditFileName(event.target.value)} />
            </FormField>

            <FormField label="3D model type">
              <Select
                value={modelTypeOptions.find((option) => option.value === editModelType)}
                options={modelTypeOptions}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={(option) => {
                  if (!option) return;
                  if (option.value !== editModelType) {
                    setAssociationItemId("");
                    setAssociationOptions([]);
                  }
                  setEditModelType(option.value);
                }}
                isClearable={false}
              />
            </FormField>

            <FormField label="Features (JSON)">
              <Textarea value={editFeatures} onChange={(event) => setEditFeatures(event.target.value)} />
            </FormField>

            {/* 🎯 SELETTORE GENERE CONNETTORE (MASCHIO / FEMMINA) */}
            <FormField label="Connector Side / Gender">
              <select
                value={connectorData.side}
                onChange={(e) => updateConnectorSideInFeaturesJson(e.target.value as ConnectorGender)}
                style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #d1d5db" }}
              >
                <option value="female">Female (Femmina ♀)</option>
                <option value="male">Male (Maschio ♂)</option>
                <option value="both">Both (Entrambi)</option>
              </select>
            </FormField>

            {/* CAMPI PATTERN DENTRO LE HOLES */}
            <FormField label="Holes Pattern Layout">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                <label>
                  Module Count
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={moduleLayout.count}
                    disabled={hasInvalidFeatures}
                    onChange={(e) => updateLayoutInFeaturesJson("count", e.target.valueAsNumber)}
                  />
                </label>

                <label>
                  Module Pitch (mm)
                  <Input
                    type="number"
                    step="0.1"
                    value={moduleLayout.pitch_mm}
                    disabled={hasInvalidFeatures}
                    onChange={(e) => updateLayoutInFeaturesJson("pitch_mm", e.target.valueAsNumber)}
                  />
                </label>
              </div>
            </FormField>

            <FormField label="Repeat Axis (X, Y, Z)">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {(["X", "Y", "Z"] as const).map((axis, index) => (
                  <label key={`repeat-${axis}`}>
                    {axis}
                    <Input
                      type="number"
                      step="1"
                      value={moduleLayout.repeat_axis?.[index] ?? (index === 0 ? 1 : 0)}
                      disabled={hasInvalidFeatures}
                      onChange={(e) => {
                        const newAxis = [...(moduleLayout.repeat_axis ?? [1, 0, 0])] as [number, number, number];
                        newAxis[index] = Number.isFinite(e.target.valueAsNumber) ? e.target.valueAsNumber : 0;
                        updateLayoutInFeaturesJson("repeat_axis", newAxis);
                      }}
                    />
                  </label>
                ))}
              </div>
            </FormField>

            {/* CONTROLLI ROTAZIONE COMPONENTE */}
            <FormField label="Component rotation (degrees X, Y, Z)">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {(["X", "Y", "Z"] as const).map((axis, index) => (
                  <label key={`comp-${axis}`}>
                    {axis}
                    <Input
                      type="number"
                      step="1"
                      value={componentRotation[index]}
                      disabled={hasInvalidFeatures}
                      onChange={(event) =>
                        updateRotationInFeaturesJson("component", index as 0 | 1 | 2, event.target.valueAsNumber)
                      }
                    />
                  </label>
                ))}
              </div>
            </FormField>

            {/* CONTROLLI ROTAZIONE FORI */}
            <FormField label="All holes rotation (degrees X, Y, Z)">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {(["X", "Y", "Z"] as const).map((axis, index) => (
                  <label key={`holes-${axis}`}>
                    {axis}
                    <Input
                      type="number"
                      step="1"
                      value={holesRotation[index]}
                      disabled={hasInvalidFeatures}
                      onChange={(event) =>
                        updateRotationInFeaturesJson("holes", index as 0 | 1 | 2, event.target.valueAsNumber)
                      }
                    />
                  </label>
                ))}
              </div>
            </FormField>

            <FormField label={`Associate to ${entityLabels[editModelType]} item (optional)`}>
              <AsyncSelect<SelectOption, false>
                cacheOptions
                defaultOptions={associationOptions}
                loadOptions={(inputValue) => searchAssociationOptions(editModelType, inputValue)}
                placeholder={`Search ${entityLabels[editModelType]} code...`}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                isClearable
                onChange={(option) => {
                  setAssociationItemId(option?.value ?? "");
                }}
                value={associationOptions.find((option) => option.value === associationItemId) ?? null}
              />
            </FormField>

            <FormField label="Replace file (optional)">
              <FileUploader accept=".glb" onSelect={setEditFile} placeholder="Select a replacement .glb file" />
            </FormField>

            <FormField label={`3D preview (${calculatedHoles.length} holes calculated)`}>
              <Model3DPreview
                format={editFile ? getModelFormat(editFile.name) : modelToEdit.modelFormat}
                url={previewUrl}
                connectorModule={connectorModuleData}
                connector={connectorData}
                holes={calculatedHoles}
                componentRotation={componentRotation}
                holesRotation={holesRotation}
              />
            </FormField>
          </FormFieldsContainer>
        </ModalDialog>
      )}

      {modelToDelete && (
        <ModalDialog
          title={`Delete ${modelToDelete.modelFile}?`}
          onClose={() => setModelToDelete(undefined)}
          isSubmitting={false}
          buttons={[
            <Button key={0} variant="secondary" onClick={() => setModelToDelete(undefined)}>
              {i18n.translation.common.cancel}
            </Button>,
            <Button key={1} variant="primary" onClick={deleteModel}>
              {i18n.translation.common.delete}
            </Button>,
          ]}
        >
          This operation removes the binary model from the database.
        </ModalDialog>
      )}
    </>
  );
};

export default Admin3DModelsTab;
